import axios from 'axios';
import {
  authHeaders,
  deleteAuthenticated,
  getAuthenticated,
  patchAuthenticated,
  postAuthenticated,
} from './http';

const NIGHTS_API_BASE = '/api/nights';

const localTimestampParam = (date = new Date()) => {
  const localDate = new Date(date);
  localDate.setSeconds(0, 0);

  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:00`;
};

const withClientNow = (params = {}) => ({
  ...params,
  now: params.now || localTimestampParam(),
});

export const fetchUpcomingNight = (params = {}) =>
  getAuthenticated(`${NIGHTS_API_BASE}/upcoming`, { params: withClientNow(params) });

export const fetchNights = (params = {}) =>
  getAuthenticated(NIGHTS_API_BASE, { params: withClientNow(params) });

export const fetchNight = (id) => getAuthenticated(`${NIGHTS_API_BASE}/${id}`);

export const fetchNightPosition = (id, params = {}) =>
  getAuthenticated(`${NIGHTS_API_BASE}/position/${id}`, { params: withClientNow(params) });

export const createNight = (night) => postAuthenticated(NIGHTS_API_BASE, night);

export const updateNight = (id, night) => patchAuthenticated(`${NIGHTS_API_BASE}/${id}`, night);

export const createNightShareInvitation = (id) =>
  postAuthenticated(`${NIGHTS_API_BASE}/${id}/share-invitation`, {});

export const fetchNightVotes = (id) => getAuthenticated(`${NIGHTS_API_BASE}/${id}/votes`);

export const updateNightVotes = (id, gameIds) =>
  patchAuthenticated(`${NIGHTS_API_BASE}/${id}/votes`, { gameIds });

export const syncNightCalendar = (id) =>
  postAuthenticated(`${NIGHTS_API_BASE}/${id}/calendar/sync`, {});

export const removeNightCalendar = (id) =>
  deleteAuthenticated(`${NIGHTS_API_BASE}/${id}/calendar`);

const icsFilenameFromDisposition = (disposition, id) => {
  const match = /filename="?([^";]+)"?/i.exec(disposition || '');

  return match?.[1] || `the-tavern-night-${id}.ics`;
};

const isAndroid = () =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');

const googleCalendarDate = (date) =>
  date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

const nightCalendarDates = (night) => {
  const startsAt = new Date(night?.scheduledAt || night?.scheduledAtUtc || night?.dateTime || '');

  if (Number.isNaN(startsAt.getTime())) {
    return null;
  }

  const endsAt = new Date(startsAt.getTime() + 3 * 60 * 60 * 1000);

  return `${googleCalendarDate(startsAt)}/${googleCalendarDate(endsAt)}`;
};

const nightLocation = (night) =>
  [night?.locationName, night?.locationAddress].filter(Boolean).join(' - ');

const googleCalendarUrl = (night, origin = window.location.origin) => {
  const dates = nightCalendarDates(night);

  if (!night?.id || !dates) {
    return '';
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: night.title || 'The Tavern',
    dates,
    details: `Soiree jeux organisee avec The Tavern.\n\nOuvrir la table :\n${origin}/nights?focusNightId=${night.id}`,
  });
  const location = nightLocation(night);

  if (location) {
    params.set('location', location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const openExternalCalendar = (url) => {
  const link = document.createElement('a');

  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const downloadCalendarBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadNightCalendarFile = async (id, night = null) => {
  const googleUrl = isAndroid() ? googleCalendarUrl(night) : '';

  if (googleUrl) {
    openExternalCalendar(googleUrl);
    return { filename: null, action: 'google-calendar' };
  }

  const response = await axios.get(`${NIGHTS_API_BASE}/${id}/calendar/ics`, {
    headers: authHeaders(),
    responseType: 'blob',
  });
  const filename = icsFilenameFromDisposition(response.headers?.['content-disposition'], id);

  downloadCalendarBlob(response.data, filename);
  return { filename, action: 'downloaded' };
};

export const acceptNightInvitation = (id) =>
  postAuthenticated(`${NIGHTS_API_BASE}/${id}/invitation/accept`, {});

export const declineNightInvitation = (id) =>
  postAuthenticated(`${NIGHTS_API_BASE}/${id}/invitation/decline`, {});

export const deleteNight = (id, options = {}) =>
  deleteAuthenticated(`${NIGHTS_API_BASE}/${id}`, { data: options });
